import React, { useState } from "react";
import { FaMapMarkerAlt, FaClock, FaPaperPlane } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { submitEnquiry } from "../../services/enquiry";

type FormData = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};
 
const ContactSection: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitStatus({ type: 'error', message: t("Please fill required fields") });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    const pagePath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    const payload = {
      fullName: formData.name,
      email: formData.email,
      phone: formData.phone.trim(),
      subject: formData.subject || "General Inquiry",
      message: formData.message,
      source: "Website UAE - Help & Support",
      sourcePage: "Help & Support",
      leadCountry: "UAE",
      country: "UAE",
      pagePath,
      pageUrl: window.location.href,
      utmSource: "website_uae",
      utmMedium: "contact_form",
      utmCampaign: "support_enquiry",
    };

    const result = await submitEnquiry(payload);
    setIsSubmitting(false);

    if (result.success) {
      setSubmitStatus({ type: 'success', message: t("Message sent successfully!") });
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } else {
      setSubmitStatus({ type: 'error', message: result.message || t("Failed to send. Please try again.") });
    }
  };

  return (
    <section className="py-12 md:py-20 bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-20">
          <div className="w-full lg:w-5/12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-dark mb-6">
              {t("Visit Our Office")}
            </h2>

            <p className="text-gray-600 mb-8">
              {t("Visit our luxury lounge in Dubai. Walk-ins welcome.")}
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center text-brand-pink">
                  <FaMapMarkerAlt />
                </div>

                <div>
                  <h4 className="font-bold text-brand-dark mb-1">
                    {t("India Headquarters")}
                  </h4>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    Kavarana Mansion behind Pune Asisad Bus stop <br />
                      Dadar East, <br />
                    Mumbai, Maharashtra
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center text-brand-pink">
                  <FaClock />
                </div>

                <div>
                  <h4 className="font-bold text-brand-dark mb-1">
                    {t("Working Hours")}
                  </h4>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    Mon-Fri: 9 AM - 6 PM <br />
                    Sat: 10 AM - 2 PM <br />
                    Sun: Closed (Online 24/7)
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full h-64 rounded-2xl overflow-hidden relative group shadow">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.0!2d72.8447!3d19.0176!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7cf0f0f0f0f0f%3A0x0!2sKavarana+Mansion%2C+Dadar+East%2C+Mumbai%2C+Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="600"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Kavarana Mansion, Dadar East, Mumbai"
                className="w-full h-full"
              />
            </div>
          </div>

          <div className="w-full lg:w-7/12">
            <div className="insp-card p-8 md:p-10 bg-white rounded-2xl shadow">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-brand-dark mb-2">
                {t("Send an Inquiry")}
              </h3>

              <p className="text-gray-500 mb-8">
                {t("We'll get back within 2 hours.")}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t("Full Name")}
                    className="insp-input"
                    required
                  />

                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("Email")}
                    className="insp-input"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t("Phone")}
                    className="insp-input"
                    required
                  />

                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="insp-input"
                  >
                    <option value="">{t("Select subject")}</option>
                    <option value="package">{t("Holiday Package")}</option>
                    <option value="visa">{t("Visa Assistance")}</option>
                    <option value="corporate">{t("Corporate Travel")}</option>
                    <option value="support">{t("Support")}</option>
                  </select>
                </div>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder={t("Your message...")}
                  className="insp-input resize-none"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="insp-btn-primary w-full py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t("Sending...") : t("Send Message")} <FaPaperPlane />
                </button>

                {submitStatus && (
                  <div
                    className={`rounded-lg border px-4 py-3 text-center text-sm ${
                      submitStatus.type === 'success'
                        ? 'border-green-100 bg-green-50 text-green-700'
                        : 'border-red-100 bg-red-50 text-red-700'
                    }`}
                    role={submitStatus.type === 'success' ? 'status' : 'alert'}
                  >
                    {submitStatus.message}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
